import type {
  StudyNoteInput,
  StudyNoteType,
  StudyTemplateKey
} from '@/types/study'
import { normalizeTemplateKeys } from './study-card-generation'

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

function compactSource(source: Record<string, unknown>) {
  const rest = { ...source }
  delete rest.sources
  return rest
}

function sourceList(
  source: Record<string, unknown>
): Record<string, unknown>[] {
  const sources = source.sources
  const listed = Array.isArray(sources)
    ? sources.filter(isRecord).map(compactSource)
    : []
  const compact = compactSource(source)
  return Object.keys(compact).length ? [compact, ...listed] : listed
}

export function mergeStudyNoteTags(
  existing: string[] | null | undefined,
  incoming: string[] | null | undefined
): string[] {
  return Array.from(
    new Set([...(existing ?? []), ...(incoming ?? [])].filter(Boolean))
  )
}

export function mergeStudyNoteSource(
  existing: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const existingSource = isRecord(existing) ? existing : {}
  const incomingSource = isRecord(incoming) ? incoming : {}
  const sources = [...sourceList(existingSource), compactSource(incomingSource)]
    .filter((source) => Object.keys(source).length > 0)
    .reduce<Record<string, unknown>[]>((acc, source) => {
      const key = stableStringify(source)
      if (!acc.some((item) => stableStringify(item) === key)) acc.push(source)
      return acc
    }, [])

  const merged = { ...incomingSource, ...existingSource }
  if (sources.length > 1 || Array.isArray(existingSource.sources)) {
    merged.sources = sources
  }
  return merged
}

function hasMeaningfulValue(value: unknown) {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  return value !== null && value !== undefined
}

export function mergeStudyNoteFields(
  existing: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const merged = { ...(incoming ?? {}), ...(existing ?? {}) }
  for (const [key, value] of Object.entries(incoming ?? {})) {
    if (!hasMeaningfulValue(merged[key]) && hasMeaningfulValue(value)) {
      merged[key] = value
    }
  }
  return merged
}

export function getEnabledTemplateKeys(
  noteType: StudyNoteType,
  fields: Record<string, unknown> | null | undefined
): StudyTemplateKey[] {
  const raw = fields?.enabled_templates
  return normalizeTemplateKeys(
    noteType,
    Array.isArray(raw)
      ? raw.filter((key): key is StudyTemplateKey => typeof key === 'string')
      : undefined
  )
}

export function mergeStudyNoteInput(
  existing: {
    note_type: StudyNoteType
    fields: Record<string, unknown> | null
    source: Record<string, unknown> | null
    tags: string[] | null
  },
  incoming: StudyNoteInput
): StudyNoteInput {
  const incomingTemplates = normalizeTemplateKeys(
    incoming.noteType,
    incoming.templateKeys
  )
  const existingTemplates = getEnabledTemplateKeys(
    existing.note_type,
    existing.fields
  )
  const templateKeys = normalizeTemplateKeys(incoming.noteType, [
    ...existingTemplates,
    ...incomingTemplates
  ])

  return {
    ...incoming,
    fields: mergeStudyNoteFields(existing.fields, {
      ...incoming.fields,
      enabled_templates: templateKeys
    }),
    source: mergeStudyNoteSource(existing.source, incoming.source),
    tags: mergeStudyNoteTags(existing.tags, incoming.tags),
    templateKeys
  }
}
